import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import { io } from 'socket.io-client';
import { usersAPI, busesAPI, routesAPI, studentsAPI } from '../../services/api';

function AdminDashboard() {
  const [stats, setStats] = useState({ users: 0, buses: 0, routes: 0, students: 0 });
  const [notifications, setNotifications] = useState([]);
  const [activeRoutes, setActiveRoutes] = useState(new Set());
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    async function loadStats() {
      try {
        const [u, b, r, s] = await Promise.all([
          usersAPI.list(), busesAPI.list(), routesAPI.list(), studentsAPI.list()
        ]);
        setStats({ users: u.data.length, buses: b.data.length, routes: r.data.length, students: s.data.length });
        setActiveRoutes(new Set(r.data.map((route) => route.id)));
      } catch (err) {
        console.error('Error loading stats:', err);
      }
    }
    loadStats();
  }, []);

  useEffect(() => {
    const socket = io(window.location.origin);
    socket.on('bus_location_changed', (data) => {
      setNotifications((prev) => [{
        id: Date.now(),
        type: 'location',
        message: t('busUpdated', { id: data.bus_id.substring(0, 8) }),
        detail: `Lat: ${data.latitude}, Lng: ${data.longitude}`,
        time: new Date(),
      }, ...prev].slice(0, 50));
    });
    socket.on('student_event', (data) => {
      const msg = data.event_type === 'pickup'
        ? t('studentPickedUp', { name: data.student_name })
        : t('studentDroppedOff', { name: data.student_name });
      setNotifications((prev) => [{
        id: Date.now(),
        type: data.event_type,
        message: msg,
        detail: `Route: ${data.route_id.substring(0, 8)}...`,
        time: new Date(),
      }, ...prev].slice(0, 50));
    });
    return () => socket.disconnect();
  }, [t]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const notifColor = (type) => {
    if (type === 'pickup') return { bg: '#d4edda', border: '#28a745' };
    if (type === 'dropoff') return { bg: '#d1ecf1', border: '#007bff' };
    return { bg: '#fff3cd', border: '#ffc107' };
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1>{t('adminDashboard')}</h1>
          <p>{t('welcome', { name: user?.name })}</p>
        </div>
        <button onClick={handleLogout} style={styles.logoutBtn}>{t('logout')}</button>
      </div>

      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #007bff' }}>
          <h3>{stats.users}</h3><p>{t('users')}</p>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #28a745' }}>
          <h3>{stats.buses}</h3><p>{t('buses')}</p>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #ffc107' }}>
          <h3>{stats.routes}</h3><p>{t('routes')}</p>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #17a2b8' }}>
          <h3>{stats.students}</h3><p>{t('students')}</p>
        </div>
      </div>

      <div style={styles.navGrid}>
        <Link to="/admin/users" style={styles.navCard}>{t('manageUsers')}</Link>
        <Link to="/admin/buses" style={styles.navCard}>{t('manageBuses')}</Link>
        <Link to="/admin/routes" style={styles.navCard}>{t('manageRoutes')}</Link>
        <Link to="/admin/students" style={styles.navCard}>{t('manageStudents')}</Link>
        <Link to="/admin/tracking" style={{ ...styles.navCard, background: '#28a745' }}>{t('liveTracking')}</Link>
        <Link to="/admin/history" style={{ ...styles.navCard, background: '#17a2b8' }}>{t('tripHistory')}</Link>
      </div>

      <div style={{ marginTop: '30px', background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}>
        <h2>{t('liveNotifications')}</h2>
        {notifications.length === 0 ? (
          <p style={{ color: '#888' }}>{t('listeningEvents')}</p>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {notifications.map((n) => {
              const color = notifColor(n.type);
              return (
                <div key={n.id} style={{ padding: '10px', borderRadius: '6px', marginBottom: '6px', backgroundColor: color.bg, borderLeft: `4px solid ${color.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ fontSize: '14px' }}>{n.message}</strong>
                    <span style={{ fontSize: '12px', color: '#666' }}>{n.time.toLocaleTimeString()}</span>
                  </div>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#555' }}>{n.detail}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  logoutBtn: { padding: '8px 16px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' },
  statCard: { background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  navGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' },
  navCard: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '30px', background: '#007bff', color: '#fff', textDecoration: 'none', borderRadius: '8px', fontSize: '18px', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
};

export default AdminDashboard;
